import { useMemo } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
  Legend,
} from 'recharts';

import {
  CHART_SYMBOL_WIDTH, CHART_SYMBOL_HEIGHT,
  defaultChartAxisStyleDescription,
  defaultChartSeriesStyleDescription,
} from './common';
import { ScreenSizes, useScreenSizes } from 'common/Grid/useScreenSize';

export const LineChart = ({ data, description, showGrid = true, showTooltip = true, showLegend = true, selectedPeriod}) => {
  const screenSize = useScreenSizes();

  const yAxisLongestLabelMap = useMemo(() => {
    const resultMap = {};
    if (description.axes?.y) {
      for (const [axiskey, axisDescription] of Object.entries(description.axes?.y)) {
        const dataKey = axisDescription.dataKey;
        if (dataKey) {
          resultMap[axiskey] = Math.max(...data.map((x) => `${x[dataKey]}`.length));
        }
        else {
          const dataKeys = description.data.filter(x => x.yAxisKey === axiskey).map(x => x.dataKey);
          resultMap[axiskey] = Math.max(1, ...data.map((x) => Math.max(...dataKeys.map(k => `${x[k]}`.length))));
        }
      }
      return resultMap;
    }
    return resultMap;
  }, [data, description]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsLineChart data={data} margin={{ right: CHART_SYMBOL_WIDTH }}>
        {showGrid ? <CartesianGrid strokeDasharray="1 1" horizontal={true} vertical={true} /> : <></>}
        {showLegend ? <Legend layout="horizontal" verticalAlign="bottom" align="center" /> : <></>}
        {showTooltip ? (
          <Tooltip
            includeHidden={false}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.xValue ?? label}
            formatter={description.tooltipFormatter ?? ((value, name, props) => value)}
          />
        ) : <></>}
        {
          description.axes?.x
            ? Object.entries(description.axes.x).map(([k, d]) => {
              const style = { ...defaultChartAxisStyleDescription, ...d.style };
              return (
                <XAxis key={k} xAxisId={k} dataKey={d.dataKey}
                  tick={
                    style.showLabels
                      ? ({ index, payload, x, y }) => {
                        const weekly = selectedPeriod === "Weekly";
                        const isLast = weekly && index === data.length - 1;

                        const offsetX = weekly && index === 0 ? x + 10 : x;

                        return (
                          <text
                            x={offsetX}
                            y={y}
                            dy={16}
                            fontSize={screenSize === ScreenSizes.Small ? 12 : 15}
                            textAnchor={weekly ? (isLast ? "end" : "middle") : "end"}
                            transform={`rotate(${weekly ? 0 : -30} ${offsetX},${y})`}>
                            {payload.value}
                          </text>
                        );
                      }
                      : false
                  }
                  angle={selectedPeriod === "Weekly" ? 0 : -30}
                  interval={0}
                  textAnchor={selectedPeriod === "Weekly" ? "end" : "end"}
                  height={style.showLabels ? 7 * CHART_SYMBOL_HEIGHT : CHART_SYMBOL_HEIGHT}>
                  {
                    style.showTitle && (d.title || d.units)
                      ? <Label position={'insideBottom'}>
                        {
                          `${d.title ?? ''}${d.units ? ` (${d.units})` : ''}`
                        }
                      </Label>
                      : <></>
                  }
                </XAxis>
              );
            })
            : <></>
        }
        {
          description.axes?.y
            ? Object.entries(description.axes.y).map(([k, d]) => {
              const maxLength = yAxisLongestLabelMap[k] ?? 1;
              const style = { ...defaultChartAxisStyleDescription, ...d.style };
              return (
                <YAxis
                  key={k}
                  yAxisId={k}
                  dataKey={d.dataKey}
                  tick={style.showLabels ? { fontSize: screenSize === ScreenSizes.Small ? 12 : 15 } : false}
                  width={style.showLabels ? Math.max(screenSize === ScreenSizes.Small ? 50 : 70, (maxLength + (screenSize === ScreenSizes.Small ? 0 : 1)) * CHART_SYMBOL_WIDTH) : CHART_SYMBOL_WIDTH}
                  tickFormatter={d.tickFormatter ?? ((value) => d.units ? `${d.units}${value}` : value)}
                >
                  {
                    style.showTitle && (d.title || d.units)
                      ? <Label position={'insideLeft'} angle={-90}>
                          {`${d.title ?? ''}${d.units ? ` (${d.units})` : ''}`}
                        </Label>
                      : <></>
                  }
                </YAxis>
              );
            })
            : <></>
        }

        {
          description.data.map((d, i) => {
            const style = { ...defaultChartSeriesStyleDescription, ...d.style };
            return (
              <Line key={`${i}`}
                dataKey={d.dataKey}
                type="linear"
                dot={false}
                name={d.title}
                strokeWidth={2}
                xAxisId={d.xAxisKey}
                yAxisId={d.yAxisKey}
                stroke={style.color}
                fill={style.color} />
            );
          })
        }
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}